import { formatToPKR } from "@/lib/helper";
import { SRBReport } from "@/types/pos/srb-report";

interface SrbSummaryProps {
  data: any;
  isLoading: boolean;
  activeTab: string;
}

export default function SrbSummary({ data, isLoading, activeTab }: SrbSummaryProps) {
  if (isLoading) {
    return (
      <div className="space-y-3">
        <div className="grid gap-2 grid-cols-2 sm:grid-cols-3 md:grid-cols-4">
          <div className="animate-pulse bg-gray-100 rounded-md p-3 h-20"></div>
          <div className="animate-pulse bg-gray-100 rounded-md p-3 h-20"></div>
          <div className="animate-pulse bg-gray-100 rounded-md p-3 h-20"></div>
        </div>
      </div>
    );
  }

  // Calculate totals from summary API response
  const totalTaxAmount = data?.summary?.total_tax_amount || 0;
  const subTotalAmount = data?.summary?.total_sub_amount || 0;
  
  const rows = Array.isArray(data?.data) ? data.data : [];

  // Success Sales = tax_number is generated
  const successSalesCount = data?.summary?.success_amount || 0;
  
  // Failed SRB Sales = tax_number is not generated (null or empty string)
  const failedSalesCount = data?.summary?.failed_amount || 0;
  let salesLabel = "Total Sales";
  let taxLabel = "Total Tax";

  if (activeTab === "failed_srb") {
    salesLabel = "Failed Amount";
    taxLabel = "Failed Tax Amount";
  }

  if (activeTab === "return_srb_sale") {
    salesLabel = "Return Amount";
    taxLabel = "Return Tax Amount";
  }
  // Financial summary cards
  const financialCards = [
    {
      label: salesLabel,
      value: subTotalAmount,
      color: 'blue',
      icon: 'fa-chart-line'
    },
    {
      label: taxLabel,
      value: totalTaxAmount,
      color: 'indigo',
      icon: 'fa-percentage'
    }
  ];

  if (activeTab === "srb_sale") {
    financialCards.push({
      label: 'Success Sales',
      value: successSalesCount,
      color: 'green',
      icon: 'fa-check-circle'
    });
  }

  if (activeTab === "failed_srb") {
    financialCards.push({
      label: 'Failed SRB Sales',
      value: failedSalesCount,
      color: 'red',
      icon: 'fa-times-circle'
    });
  }

  const getColorClasses = (color: string) => {
    const colors = {
      blue: 'bg-blue-50 border-blue-200 text-blue-600',
      green: 'bg-green-50 border-green-200 text-green-600',
      red: 'bg-red-50 border-red-200 text-red-600',
      indigo: 'bg-indigo-50 border-indigo-200 text-indigo-600',
      orange: 'bg-orange-50 border-orange-200 text-orange-600',
    };
    return colors[color as keyof typeof colors] || 'bg-gray-50 border-gray-200 text-gray-600';
  };

  return (
    <div className="space-y-3">
      <div className="grid gap-2 grid-cols-2 sm:grid-cols-3 md:grid-cols-4">
        {financialCards.map((card) => (
          <div
            key={card.label}
            className={`${getColorClasses(card.color)} border rounded-md p-2 sm:p-2.5`}
          >
            <div className="flex items-center justify-between mb-1">
              <p className="text-xs font-medium truncate pr-1">{card.label}</p>
              <i className={`fa-solid ${card.icon} text-xs flex-shrink-0`}></i>
            </div>
            <p className="text-base sm:text-lg font-bold leading-tight break-all">
              {formatToPKR(card.value)}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}
